import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LEARN } from "@/constants/testIds";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const Kpi = ({ label, value, sub, testid, tone }) => (
  <div className="grid-cell">
    <div className="label-cap">{label}</div>
    <div className={`data-lg mt-2 ${tone === "up" ? "pnl-up" : tone === "down" ? "pnl-down" : ""}`} data-testid={testid}>{value}</div>
    {sub && <div className="text-[10px] mono uppercase text-muted-foreground tracking-wider mt-1">{sub}</div>}
  </div>
);

export default function Learning() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/learning/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  const outcomeData = Object.entries(s.outcomes).map(([k,v]) => ({ name: k.replace("_"," "), value: v }));
  const causeData = (s.top_primary_causes || []).map(([k,v]) => ({ name: k, value: v }));

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid={LEARN.root}>
      <h1 className="text-xl font-medium tracking-tight">Learning</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border/60">
        <Kpi label="Win Rate" value={`${s.win_rate}%`} sub={`${s.wins}/${s.total_trades}`} testid={LEARN.winRate}/>
        <Kpi label="Total P&L" value={`${s.total_pnl >= 0 ? "+" : ""}$${s.total_pnl}`} sub="Realized" testid={LEARN.totalPnl} tone={s.total_pnl >= 0 ? "up" : "down"}/>
        <Kpi label="Analyst Accuracy" value={`${s.analyst_accuracy_pct}%`} sub="PROCEED wins" testid={LEARN.analystAcc}/>
        <Kpi label="Critic Accuracy" value={`${s.critic_accuracy_pct}%`} sub="Directional calls" testid={LEARN.criticAcc}/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border/60">
        <div className="grid-cell md:col-span-2">
          <div className="label-cap mb-3">Top Root Causes</div>
          <div className="h-56">
            {causeData.length ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={40}>
                <BarChart data={causeData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#A1A1AA", fontFamily: "JetBrains Mono" }} interval={0} angle={-25} textAnchor="end" height={60}/>
                  <YAxis tick={{ fontSize: 10, fill: "#A1A1AA", fontFamily: "JetBrains Mono" }}/>
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} contentStyle={{ background: "#0C0C0C", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }}/>
                  <Bar dataKey="value">
                    {causeData.map((_, i) => <Cell key={i} fill="#0A84FF"/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No reviewed trades yet.</div>}
          </div>
        </div>

        <div className="grid-cell">
          <div className="label-cap mb-3">Outcomes</div>
          {outcomeData.length === 0 && <div className="text-xs text-muted-foreground">Complete reviews to populate.</div>}
          <div className="space-y-1.5">
            {outcomeData.map(({ name, value }) => (
              <div key={name} className="flex items-center gap-3 text-sm">
                <span className="mono uppercase text-xs w-24">{name}</span>
                <div className="flex-1 h-1.5 bg-secondary/40">
                  <div className="h-full bg-[#0A84FF]" style={{ width: `${Math.min(100, value * 20)}%` }}/>
                </div>
                <span className="mono text-xs w-6 text-right">{value}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Ignored Critic</span><span className="mono">{s.ignored_critic_count}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Ignored → loss</span><span className="mono pnl-down">{s.ignored_critic_losses}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Avg Return</span><span className="mono">{s.avg_return_pct}%</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Avg Hold</span><span className="mono">{s.avg_hold_minutes} min</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Pending Reviews</span><span className="mono text-signal-yellow">{s.pending_reviews}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
